import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

/** Корневые страницы из главного меню — без хлебных крошек */
const MENU_ROOT_PATHS = new Set(["/map", "/fishes", "/blog", "/tools", "/spots/new"]);

/** Pages that use the glass canvas background */
const GLASS_CANVAS_PATHS = new Set(["/about", "/privacy-policy"]);

function isGlassPage(pathname) {
    return GLASS_CANVAS_PATHS.has(pathname)
        || /^\/blog(?:\/[a-z0-9-]+)?$/.test(pathname)
        || /^\/fishes(?:\/[a-z0-9-]+)?$/.test(pathname)
        || /^\/tools/.test(pathname)
        || /^\/profile/.test(pathname)
        || /^\/spots\/new$/.test(pathname)
        || /^\/spots\/\d+(?:\/edit-suggestion)?$/.test(pathname)
        || /^\/forgot$/.test(pathname)
        || /^\/login$/.test(pathname)
        || /^\/register$/.test(pathname)
        || /^\/reset$/.test(pathname)
        || /^\/verify-email$/.test(pathname);
}

const SEGMENT_LABELS = {
    about: "О проекте",
    map: "Карта",
    fishes: "Рыбы",
    blog: "Блог",
    tools: "Инструменты",
    profile: "Профиль",
    new: "Новая точка",
    "edit-suggestion": "Правки",
    messages: "Сообщения",
    suggestions: "Предложения",
    favorites: "Избранные точки",
    likes: "Лайки",
    comments: "Отчёты",
    "blog-comments": "Комментарии",
    login: "Вход",
    register: "Регистрация",
    forgot: "Восстановление пароля",
    reset: "Сброс пароля",
    "verify-email": "Подтверждение email",
    settings: "Настройки",
    "privacy-policy": "Политика конфиденциальности",
    "fish-detection": "Определить рыбу",
    "sonar-upload": "Загрузка точек эхолота",
    "my-spots": "Мои точки",
};

function defaultLabel(segment) {
    if (SEGMENT_LABELS[segment]) {
        return SEGMENT_LABELS[segment];
    }
    if (/^\d+$/.test(segment)) {
        return `#${segment}`;
    }
    return segment;
}

/**
 * Сегмент «spots» в URL не показываем как «Точки»: первая крошка — «Карта» со ссылкой на /map.
 * Числовой id после spots — название точки со ссылкой на карту с ?spot_id=.
 */
function buildItems(pathname, dynamicLabels = {}) {
    const parts = pathname.split("/").filter(Boolean);
    const items = [];
    let acc = "";

    for (let i = 0; i < parts.length; i += 1) {
        const segment = parts[i];
        acc += `/${segment}`;
        const prev = parts[i - 1];

        if (i === 0 && segment === "spots") {
            items.push({ key: "crumb-spots-root", to: "/map", label: "Карта" });
            continue;
        }

        if (prev === "spots" && /^\d+$/.test(segment)) {
            const hasEditSuggestion = parts[i + 1] === "edit-suggestion";
            items.push({
                key: `crumb-spot-${segment}`,
                to: hasEditSuggestion ? `/spots/${segment}` : `/map?spot-id=${encodeURIComponent(segment)}`,
                label: dynamicLabels[`spot:${segment}`] || `Точка #${segment}`,
                truncate: hasEditSuggestion,
            });
            continue;
        }

        if (prev === "blog" && /^[a-z0-9-]+$/.test(segment)) {
            items.push({
                key: `crumb-blog-${segment}`,
                to: acc,
                label: dynamicLabels[`blog:${segment}`] || segment,
            });
            continue;
        }

        if (prev === "fishes" && /^[a-z0-9-]+$/.test(segment)) {
            items.push({
                key: `crumb-fish-${segment}`,
                to: acc,
                label: dynamicLabels[`fish:${segment}`] || segment,
            });
            continue;
        }

        items.push({
            key: acc,
            to: acc,
            label: defaultLabel(segment),
        });
    }

    return items;
}

export default function Breadcrumbs() {
    const location = useLocation();
    const [dynamicLabels, setDynamicLabels] = useState({});

    useEffect(() => {
        const blogMatch = location.pathname.match(/^\/blog\/([a-z0-9-]+)$/);
        const fishMatch = location.pathname.match(/^\/fishes\/([a-z0-9-]+)$/);
        const spotMatch = location.pathname.match(/^\/spots\/(\d+)(?:\/edit-suggestion)?$/);
        if (!blogMatch && !fishMatch && !spotMatch) {
            setDynamicLabels({});
            return undefined;
        }

        let cancelled = false;
        if (blogMatch) {
            const articleSlug = blogMatch[1];
            fetch(`${API_BASE_URL}/api/articles/${articleSlug}`)
                .then((response) => (response.ok ? response.json() : null))
                .then((article) => {
                    if (cancelled || !article?.title) return;
                    setDynamicLabels({ [`blog:${articleSlug}`]: article.title });
                })
                .catch(() => {
                    if (!cancelled) setDynamicLabels({});
                });

            return () => {
                cancelled = true;
            };
        }

        if (fishMatch) {
            const fishSlug = fishMatch[1];
            fetch(`${API_BASE_URL}/api/fish/${fishSlug}`)
                .then((response) => (response.ok ? response.json() : null))
                .then((fish) => {
                    if (cancelled || !fish?.name) return;
                    setDynamicLabels({ [`fish:${fishSlug}`]: fish.name });
                })
                .catch(() => {
                    if (!cancelled) setDynamicLabels({});
                });

            return () => {
                cancelled = true;
            };
        }

        const spotId = spotMatch[1];
        fetch(`${API_BASE_URL}/api/spots/${spotId}`, { credentials: "include" })
            .then((response) => (response.ok ? response.json() : null))
            .then((spot) => {
                const label = spot?.title || spot?.place?.name || spot?.name || "";
                if (cancelled || !label) return;
                setDynamicLabels({ [`spot:${spotId}`]: label });
            })
            .catch(() => {
                if (!cancelled) setDynamicLabels({});
            });

        return () => {
            cancelled = true;
        };
    }, [location.pathname]);

    if (MENU_ROOT_PATHS.has(location.pathname) || /^\/profile/.test(location.pathname) || /^\/spots\/new$/.test(location.pathname)) {
        return null;
    }

    const items = buildItems(location.pathname, dynamicLabels);
    if (items.length === 0) {
        return null;
    }

    const isGlass = isGlassPage(location.pathname);
    const isWide = /^\/fishes\/[a-z0-9-]+/.test(location.pathname) || /^\/tools\/.+/.test(location.pathname);
    const isSpotWide = /^\/spots\/\d+$/.test(location.pathname);
    const isSpotFormWide = /^\/spots\/\d+\/edit-suggestion$/.test(location.pathname);
    const isAuthPage = /^\/(?:forgot|login|register|reset|verify-email)$/.test(location.pathname);
    const isWidest = /^\/profile/.test(location.pathname);

    const crumbs = (
        <>
            <Link to="/">Главная</Link>
            {items.map((item, index) => (
                <React.Fragment key={item.key}>
                    <span className="breadcrumbs__sep" aria-hidden="true">›</span>
                    {index === items.length - 1 ? (
                        <span className="breadcrumbs__current" title={item.label}>{item.label}</span>
                    ) : (
                        <Link
                            to={item.to}
                            className={item.truncate ? "breadcrumbs__link breadcrumbs__link--truncate" : undefined}
                            title={item.label}
                        >
                            {item.label}
                        </Link>
                    )}
                </React.Fragment>
            ))}
        </>
    );

    return (
        <nav className={`breadcrumbs${isGlass ? " breadcrumbs--glass" : ""}${isGlass && isWide ? " breadcrumbs--glass-wide" : ""}${isGlass && isSpotWide ? " breadcrumbs--glass-spot" : ""}${isGlass && isSpotFormWide ? " breadcrumbs--glass-new-spot" : ""}${isGlass && isAuthPage ? " breadcrumbs--glass-auth" : ""}${isGlass && isWidest ? " breadcrumbs--glass-widest" : ""}`} aria-label="breadcrumbs">
            {isGlass
                ? <span className="breadcrumbs__trail">{crumbs}</span>
                : crumbs
            }
        </nav>
    );
}
